import AsyncStorage from '@react-native-async-storage/async-storage';
import { ScopedAsyncStorage } from '@/lib/storage';

const PREFERRED_NAME_KEY = '@fimfam/preferred-name';

/**
 * PreferredNameManager - Singleton
 * Manages preferred name storage locally (device only, no database sync).
 * Stores preferred name in AsyncStorage for persistence across app restarts.
 */
export class PreferredNameManager {
  private static instance: PreferredNameManager;
  private currentPreferredName: string | null = null;
  private initialized = false;
  private storage: ScopedAsyncStorage | null = null;
  private listeners: Set<() => void> = new Set();

  private constructor() {}

  /**
   * Get singleton instance
   */
  static getInstance(): PreferredNameManager {
    if (!PreferredNameManager.instance) {
      PreferredNameManager.instance = new PreferredNameManager();
    }
    return PreferredNameManager.instance;
  }

  /**
   * Set scoped storage and reset initialization
   * Call this when user or device changes
   */
  setStorage(storage: ScopedAsyncStorage): void {
    this.storage = storage;
    this.initialized = false;
    this.currentPreferredName = null;
  }

  /**
   * Initialize by loading stored preferred name
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      const storedName = this.storage
        ? await this.storage.getItem(PREFERRED_NAME_KEY)
        : await AsyncStorage.getItem(PREFERRED_NAME_KEY);
      this.currentPreferredName = storedName;
      this.initialized = true;
      this.notifyListeners();
    } catch (error) {
      console.error('[PreferredNameManager:initialize] Failed to load preferred name:', error);
      this.currentPreferredName = null;
      this.initialized = true;
      this.notifyListeners();
    }
  }

  /**
   * Subscribe to preferred name changes
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }

  /**
   * Get current preferred name
   */
  getPreferredName(): string | null {
    return this.currentPreferredName;
  }

  /**
   * Set preferred name and persist to storage
   */
  async setPreferredName(name: string | null): Promise<void> {
    try {
      this.currentPreferredName = name;
      if (name) {
        if (this.storage) {
          await this.storage.setItem(PREFERRED_NAME_KEY, name);
        } else {
          await AsyncStorage.setItem(PREFERRED_NAME_KEY, name);
        }
      } else {
        if (this.storage) {
          await this.storage.removeItem(PREFERRED_NAME_KEY);
        } else {
          await AsyncStorage.removeItem(PREFERRED_NAME_KEY);
        }
      }
      this.notifyListeners();
    } catch (error) {
      console.error('[PreferredNameManager:setPreferredName] Failed to save preferred name:', error);
      throw error;
    }
  }

  /**
   * Remove preferred name
   */
  async removePreferredName(): Promise<void> {
    await this.setPreferredName(null);
  }
}
